// RITA-1543 — Texte der Karte „Vertragserstellung“ und des Fensters „Vertrag erstellen“.
// Wortlaut nach der von Julia am 19.10.2026 freigegebenen Skizze (Vertragserstellung-3a-Skizze).
// RITA-1552: Die Texte je Art kennen auch das Anschreiben Rücksendung (Rücklauf beim Berater).
import type { ContractCreationKind } from 'global-intern-types';
import type { Vertragsauswahl, VertragsauswahlDokument, Vorlagenkurz } from '../api/vertragserstellung';
import { VORLAGEN_GESCHLECHTER } from '../../templates/services/vorlagenZuordnung';

export const KARTEN_TITEL = 'Vertragserstellung';
export const KNOPF_TEXT = 'Vertrag erstellen';
export const FENSTER_TITEL = 'Vertrag erstellen';
export const SCHLIESSEN_TEXT = 'Schließen';
export const BITTE_SPEICHERN =
  'Bitte zuerst speichern. Die Vorlagen und Daten richten sich nach den gespeicherten Angaben.';
export const NEUER_BERATER = 'Steht zur Verfügung, sobald der Berater gespeichert ist.';
export const WIRD_GESUCHT = 'Passende Vorlagen werden gesucht …';
export const LADEFEHLER_KARTE = 'Die passenden Vorlagen konnten nicht geladen werden.';
export const LADEFEHLER_FENSTER = 'Anschreiben und Vertrag konnten nicht geladen werden.';
export const ERNEUT_VERSUCHEN = 'Erneut versuchen';
export const EINZELHEITEN_IM_FENSTER = 'Einzelheiten zeigt das Fenster.';
export const VERTRAGSINHABER_ART_FEHLT = 'Art des Vertragsinhabers fehlt.';
// RITA-1572 (Text von Julia am 29.10.2026 freigegeben): Vorlagen gelten immer für genau eine Vertragsart.
export const VERTRAGSART_FEHLT =
  'Beim Berater ist keine Vertragsart eingetragen. Vorlagen gelten immer für eine bestimmte Vertragsart.';
export const VERTRAGSART_EINTRAGEN = 'Bitte zuerst beim Berater die Vertragsart eintragen.';
export const VERTRAGSINHABER_ART_EINTRAGEN =
  'Bitte beim Berater für den abweichenden Vertragsinhaber Mann, Frau oder Firma angeben.';
export const VERTRAGSINHABER_ART_SPERRE =
  'Bis dahin kann kein Vertrag erstellt werden. Danach speichern und dieses Fenster neu öffnen.';
export const FEHLENDE_ANGABEN_TITEL = 'Diese Angaben fehlen. Die Stellen bleiben leer:';
export const FEHLENDE_ANGABEN_FUSS = 'Nach dem Eintragen beim Berater speichern und das Fenster neu öffnen.';
export const WIRD_GELADEN = 'Anschreiben und Vertrag werden geladen …';
export const LOGOS_FEHLER =
  'Die Logos der Vertragsarten konnten nicht geladen werden. Die Kopfzeile zeigt deshalb kein Logo.';
// RITA-1552 (Skizze Rücklauf, Bild D): Lade- und Fehlertext des Fensters „Rücksendung drucken“ (eine Spalte).
export const RUECKSENDUNG_WIRD_GELADEN = 'Das Rücksendungs-Anschreiben wird geladen …';
export const LADEFEHLER_RUECKSENDUNG = 'Das Rücksendungs-Anschreiben konnte nicht geladen werden.';

/** Kurzname der Art in Karte und Meldungen. */
export const ART_KURZ: Record<ContractCreationKind, string> = {
  dispatch_letter: 'Anschreiben',
  contract: 'Vertrag',
  return_letter: 'Rücksendung',
};

/** Spaltentitel im Fenster ohne gefundene Vorlage. */
export const ART_TITEL: Record<ContractCreationKind, string> = {
  dispatch_letter: 'Anschreiben Versand',
  contract: 'Vertrag',
  return_letter: 'Anschreiben Rücksendung',
};

/** Wo die Angabe gebraucht wird (Liste fehlender Angaben). */
export const ART_STELLE: Record<ContractCreationKind, string> = {
  dispatch_letter: 'im Anschreiben',
  contract: 'im Vertrag',
  return_letter: 'in der Rücksendung',
};

/** RITA-1552: Art aus einer Serverantwort (etwa `kind` eines Fehlers); unbekannte Werte ergeben null. */
export const alsArt = (wert: unknown): ContractCreationKind | null =>
  typeof wert === 'string' && Object.prototype.hasOwnProperty.call(ART_KURZ, wert)
    ? (wert as ContractCreationKind)
    : null;

/** Titel der Meldung, wenn keine Vorlage passt (eigene Wörter je Art wegen der Artikel). */
const KEINE_PASSENDE_TITEL: Record<ContractCreationKind, string> = {
  dispatch_letter: 'Kein passendes Anschreiben gefunden.',
  contract: 'Kein passender Vertrag gefunden.',
  return_letter: 'Kein passendes Rücksendungs-Anschreiben gefunden.',
};

/** Welche Vorlage gesucht wurde („Gesucht wurde eine …“); RITA-1599 auch im Hinweis nach dem Speichern beim Berater. */
export const GESUCHTE_VORLAGE: Record<ContractCreationKind, string> = {
  dispatch_letter: `Vorlage „${ART_TITEL.dispatch_letter}“`,
  contract: 'Vertragsvorlage',
  return_letter: `Vorlage „${ART_TITEL.return_letter}“`,
};

// RITA-1584: dieselben Bezeichnungen wie bei der Vorlagenzuordnung (eine Quelle für Auswahl, Liste und Meldungen).
const FUER: Record<string, string> = VORLAGEN_GESCHLECHTER;
const GESCHLECHT: Record<string, string> = { female: 'weiblich', male: 'männlich' };

export const fuerBerater = (gender: string | null): string => (gender ? FUER[gender] : 'ohne Geschlecht');

/** RITA-1572: Beim Berater ist keine Vertragsart eingetragen – dann passt keine Vorlage. */
export const vertragsartFehlt = (auswahl: Vertragsauswahl): boolean => auswahl.contract_type_name === null;

/** Gemeinsame Kriterien; die Vertragspartner-Art kann von der Anschreiben-Zuordnung abweichen. */
export function passendFuer(auswahl: Vertragsauswahl): string {
  const teile = [auswahl.branch_office_name ?? 'unbekannte Firma', auswahl.contract_type_name ?? 'ohne Vertragsart'];
  return `Passend für ${teile.join(' · ')}:`;
}

/** Teil der Kartenzeile je Art; `warnung` = orange hervorheben. */
export function kartenteil(dokument: VertragsauswahlDokument): { text: string; warnung: boolean } {
  if (dokument.status === 'found' && dokument.template) return { text: dokument.template.name, warnung: false };
  if (dokument.status === 'ambiguous') {
    return {
      text: `${ART_KURZ[dokument.kind]}: ${dokument.candidates.length} Vorlagen passen gleich gut`,
      warnung: true,
    };
  }
  return { text: `${ART_KURZ[dokument.kind]}: keine passende Vorlage`, warnung: true };
}

/**
 * RITA-1552 (Abschlussreview MR 2, F3): Das Fenster „Vertrag erstellen“ zeigt die Rücksendung nicht. Hat in der Karte nur
 * das Anschreiben Rücksendung eine Warnung, nennt der Hinweis deshalb das Fenster „Rücksendung drucken“ (Titel wie
 * RUECKSENDUNG_FENSTER_TITEL, hier ausgeschrieben – vertragsdruckTexte.ts importiert diese Datei).
 */
export const EINZELHEITEN_IM_RUECKSENDUNGSFENSTER = 'Einzelheiten zeigt das Fenster „Rücksendung drucken“.';

/**
 * Hinweis unter „Passend für …“ (null ohne Warnung): nur die Rücksendung → das Fenster „Rücksendung drucken“; sonst –
 * auch wenn zusätzlich die Rücksendung warnt – der allgemeine Satz aus 3a. Zwei fast gleiche Sätze hintereinander
 * läsen sich wie ein Fehler; welches Dokument betroffen ist, zeigt der gelbe Teil davor.
 */
export function einzelheitenHinweis(dokumente: VertragsauswahlDokument[]): string | null {
  const mitWarnung = dokumente.filter((dokument) => kartenteil(dokument).warnung);
  if (mitWarnung.length === 0) return null;
  return mitWarnung.every((dokument) => dokument.kind === 'return_letter')
    ? EINZELHEITEN_IM_RUECKSENDUNGSFENSTER
    : EINZELHEITEN_IM_FENSTER;
}

/** „Gefunden anhand von: Firma … · Vertragsart … · Geschlecht …“ (die Werte fett, daher als Teile). */
export function gefundenAnhand(auswahl: Vertragsauswahl, mitGeschlecht = true): Array<{ name: string; wert: string }> {
  return [
    { name: 'Firma', wert: auswahl.branch_office_name ?? 'nicht verfügbar' },
    { name: 'Vertragsart', wert: auswahl.contract_type_name ?? 'nicht eingetragen' },
    ...(mitGeschlecht
      ? [{ name: 'Geschlecht', wert: auswahl.gender ? GESCHLECHT[auswahl.gender] : 'nicht eingetragen' }]
      : []),
  ];
}

/** „Mein Urlaubsglück GmbH · Standard · weiblich“ – die Werte von „Gefunden anhand von“ in einer Zeile. */
export const kriterienText = (auswahl: Vertragsauswahl): string =>
  gefundenAnhand(auswahl)
    .map((teil) => teil.wert)
    .join(' · ');

/**
 * RITA-1552 (Skizze Rücklauf, Bild D c): Hinweis, wenn kein Rücksendungs-Anschreiben passt – im Fenster „Rücksendung
 * drucken“ und auf der Bearbeiten-Seite. `kriterien` wie kriterienText.
 */
export const keinRuecksendungsanschreibenText = (beraterName: string, kriterien: string) =>
  `Für ${beraterName} passt gerade kein Rücksendungs-Anschreiben (${kriterien}). Bitte unter Einstellungen › Vorlagen › Vertragsvorlagen eine Vorlage der Art „${ART_TITEL.return_letter}“ zuordnen.`;

/** Vertragspartner getrennt vom Empfänger des Anschreibens. */
export function vertragMitText(name: string, abweichenderInhaber: boolean): string {
  return `Vertrag mit: ${name.trim() || 'nicht eingetragen'} (${abweichenderInhaber ? 'abweichender Vertragsinhaber' : 'Berater/in'})`;
}

/** Jede Spalte nennt die für sie maßgebliche Zuordnung; Anschreiben bleiben beim Berater. */
export function spaltenKriterien(auswahl: Vertragsauswahl, kind: ContractCreationKind, beraterName: string): string {
  if (kind === 'contract') {
    return auswahl.business_owner_type_missing
      ? VERTRAGSINHABER_ART_FEHLT
      : `Für: ${fuerBerater(auswahl.contract_gender)}`;
  }
  return `An: ${beraterName.trim() || 'nicht eingetragen'} · Für: ${fuerBerater(auswahl.gender)}`;
}

/** Zuordnung einer Vorlage in Kurzform, z. B. „Mein Urlaubsglück GmbH · Standard · Beraterin“. */
export function zuordnungKurz(vorlage: Vorlagenkurz): string {
  const zuordnung = vorlage.assignment;
  if (!zuordnung) return 'ohne Zuordnung';
  return [zuordnung.branch_office_name, zuordnung.contract_type_name, FUER[zuordnung.gender] ?? zuordnung.gender].join(
    ' · ',
  );
}

export interface Meldung {
  titel: string;
  zeilen: string[];
  /** Betroffene Vorlagen (zwei gleich gute). */
  vorlagen?: string[];
}

/** Meldung statt Vorschau, wenn keine oder mehrere gleich gute Vorlagen passen. */
export function auswahlMeldung(auswahl: Vertragsauswahl, dokument: VertragsauswahlDokument): Meldung {
  if (dokument.kind === 'contract' && auswahl.business_owner_type_missing) {
    return { titel: VERTRAGSINHABER_ART_FEHLT, zeilen: [VERTRAGSINHABER_ART_EINTRAGEN, VERTRAGSINHABER_ART_SPERRE] };
  }
  if (dokument.status === 'ambiguous') {
    return {
      titel: `${dokument.candidates.length} Vorlagen passen gleich gut.`,
      zeilen: ['Keine ist eindeutig genauer. Bitte bei einer der Vorlagen die Zuordnung ändern.'],
      vorlagen: dokument.candidates.map((vorlage) => `„${vorlage.name}“ (${zuordnungKurz(vorlage)})`),
    };
  }
  const titel = KEINE_PASSENDE_TITEL[dokument.kind];
  // RITA-1572: Ohne Vertragsart passt keine Vorlage, auch nicht „Berater/in – automatisch“ – daher vor dem Geschlecht.
  if (vertragsartFehlt(auswahl)) return { titel, zeilen: [VERTRAGSART_FEHLT, VERTRAGSART_EINTRAGEN] };
  const art = GESUCHTE_VORLAGE[dokument.kind];
  const gender = dokument.kind === 'contract' ? auswahl.contract_gender : auswahl.gender;
  if (gender === null) {
    return {
      titel,
      zeilen: [
        'Beim Berater ist kein Geschlecht eingetragen. Deshalb kommen nur Vorlagen für „Berater/in – automatisch“ in Frage – davon gibt es keine.',
        'Bitte beim Berater das Geschlecht eintragen.',
      ],
    };
  }
  return {
    titel,
    zeilen: [
      `Gesucht wurde eine ${art} für ${auswahl.branch_office_name ?? 'die Firma des Beraters'}, Vertragsart „${auswahl.contract_type_name}“, für ${fuerBerater(gender)}${gender === 'company' ? '' : ' oder „Berater/in – automatisch“'}. Es gibt keine.`,
      'Bitte unter Einstellungen › Vorlagen › Vertragsvorlagen einer Vorlage diese Zuordnung geben.',
    ],
  };
}

/** Meldung, wenn die gewählte Vorlage nicht als Seite gelesen werden kann (Skizze F9). */
export function nichtLesbarMeldung(name: string): Meldung {
  return {
    titel: `Die Vorlage „${name}“ kann nicht angezeigt werden.`,
    zeilen: [
      'Sie ist im alten Format gespeichert oder beschädigt. Bitte die Vorlage neu anlegen oder ihr die Zuordnung wegnehmen.',
    ],
  };
}

/** „Vorlage: Kooperationsvertrag MUGL weiblich · 11 Seiten“ */
export function vorlageUntertitel(name: string, seitenzahl: number): string {
  if (seitenzahl <= 0) return `Vorlage: ${name}`;
  return `Vorlage: ${name} · ${seitenzahl} ${seitenzahl === 1 ? 'Seite' : 'Seiten'}`;
}
